import { useState, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isToday } from 'date-fns';
import { pl } from 'date-fns/locale';
import { useClosuresInRange } from '@/hooks/useClosures';
import { useChildrenEnrolledActivities, getActivityDatesInRange } from '@/hooks/useAccounting';
import { supabase } from '@/lib/supabase';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { getBackgroundStyle } from '@/components/ChildCardBackgrounds';

export default function ClosuresCalendar({ branchId, childIds = [], backgroundPreset }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Swipe gesture state
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [slideDirection, setSlideDirection] = useState(null); // 'left' or 'right'
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const containerRef = useRef(null);
  const minSwipeDistance = 50;

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const startStr = format(monthStart, 'yyyy-MM-dd');
  const endStr = format(monthEnd, 'yyyy-MM-dd');

  // Fetch closures for the branch
  const { data: closures } = useClosuresInRange(branchId, startStr, endStr);

  // Fetch child absences for the month (parent-reported / planned)
  const { data: absences } = useQuery({
    queryKey: ['child-absences', childIds, startStr, endStr],
    queryFn: async () => {
      if (!childIds.length) return [];
      const { data, error } = await supabase
        .from('absences')
        .select('absence_date, child_id, reason, is_planned')
        .in('child_id', childIds)
        .gte('absence_date', startStr)
        .lte('absence_date', endStr);
      if (error) throw error;
      return data;
    },
    enabled: childIds.length > 0,
  });

  // Fetch teacher-marked absences (daily_updates where attendance = false)
  const { data: teacherMarkedAbsences } = useQuery({
    queryKey: ['daily_updates', 'absent', childIds, startStr, endStr],
    queryFn: async () => {
      if (!childIds.length) return [];
      const { data, error } = await supabase
        .from('daily_updates')
        .select('date, child_id')
        .in('child_id', childIds)
        .eq('attendance', false)
        .gte('date', startStr)
        .lte('date', endStr);
      if (error) throw error;
      return (data || []).map((row) => ({
        absence_date: row.date,
        child_id: row.child_id,
        reason: 'Zaznaczona w dzienniku',
      }));
    },
    enabled: childIds.length > 0,
  });

  // Merge reported absences and teacher-marked absences (per-date: reported first, then teacher-marked)
  const mergedAbsences = useMemo(() => {
    const reported = absences || [];
    const teacherMarked = teacherMarkedAbsences || [];
    const byDate = new Map();
    for (const a of reported) {
      const key = `${a.absence_date}-${a.child_id}`;
      if (!byDate.has(key)) byDate.set(key, a);
    }
    for (const a of teacherMarked) {
      const key = `${a.absence_date}-${a.child_id}`;
      if (!byDate.has(key)) byDate.set(key, a);
    }
    return Array.from(byDate.values());
  }, [absences, teacherMarkedAbsences]);

  // Build lookup maps
  const closureDates = new Set(closures?.map(c => c.closure_date) || []);
  const closureMap = Object.fromEntries(closures?.map(c => [c.closure_date, c]) || []);
  const absenceDates = new Set(mergedAbsences.map(a => a.absence_date));
  const absenceMap = useMemo(() => {
    const map = {};
    for (const a of mergedAbsences) {
      if (!map[a.absence_date]) map[a.absence_date] = a;
    }
    return map;
  }, [mergedAbsences]);

  // Fetch enrolled activities for children
  const { data: enrolledActivities } = useChildrenEnrolledActivities(childIds);

  // Build activity dates lookup (excluding closure days)
  const activityData = useMemo(() => {
    if (!enrolledActivities?.length) {
      return { activityDates: new Map(), activityList: [] };
    }

    const activityDates = new Map();
    const allActivities = [];

    for (const assignment of enrolledActivities) {
      const { cost_elements: ce, child_id, effective_until } = assignment;
      if (!ce) continue;

      // Get dates for this activity in the current month
      const dates = getActivityDatesInRange(
        ce.schedule_type,
        ce.schedule_config,
        monthStart,
        monthEnd
      );

      for (const dateStr of dates) {
        // Skip if it's a closure day (activity won't happen)
        if (closureDates.has(dateStr)) continue;

        // Skip if past effective_until date (child was unenrolled)
        if (effective_until && dateStr > effective_until) continue;

        // Add to date map
        if (!activityDates.has(dateStr)) {
          activityDates.set(dateStr, []);
        }
        activityDates.get(dateStr).push({
          name: ce.name,
          childId: child_id,
          costElementId: ce.id
        });

        // Add to list for upcoming section
        allActivities.push({
          date: dateStr,
          name: ce.name,
          childId: child_id
        });
      }
    }

    return { activityDates, activityList: allActivities };
  }, [enrolledActivities, closureDates, monthStart, monthEnd]);

  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDay = monthStart.getDay(); // 0 = Sunday
  const paddingDays = startDay === 0 ? 6 : startDay - 1; // Adjust for Monday start

  const navigateMonth = (direction) => {
    if (isAnimating) return;

    setSlideDirection(direction);
    setIsAnimating(true);

    // After animation starts, update the month
    setTimeout(() => {
      setCurrentMonth(d => new Date(
        d.getFullYear(),
        d.getMonth() + (direction === 'left' ? 1 : -1)
      ));
    }, 150);

    // Reset animation state
    setTimeout(() => {
      setIsAnimating(false);
      setSlideDirection(null);
    }, 300);
  };

  const prevMonth = () => navigateMonth('right');
  const nextMonth = () => navigateMonth('left');

  // Touch handlers for swipe navigation
  const handleTouchStart = (e) => {
    if (isAnimating) return;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    if (!touchStartX.current || isAnimating) return;

    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const diffX = currentX - touchStartX.current;
    const diffY = currentY - touchStartY.current;

    // Only track horizontal swipes (prevent vertical scroll interference)
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
      e.preventDefault();
      // Limit the swipe offset for visual feedback
      setSwipeOffset(Math.max(-100, Math.min(100, diffX)));
    }
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || isAnimating) {
      touchStartX.current = null;
      touchStartY.current = null;
      return;
    }

    const swipeDistance = swipeOffset;
    setSwipeOffset(0);
    touchStartX.current = null;
    touchStartY.current = null;

    if (Math.abs(swipeDistance) >= minSwipeDistance) {
      if (swipeDistance > 0) {
        // Swipe right = previous month
        prevMonth();
      } else {
        // Swipe left = next month
        nextMonth();
      }
    }
  };

  // Get upcoming events (from this month) - closures, absences, and activities
  const today = new Date();
  const todayStr = format(today, 'yyyy-MM-dd');
  const upcomingClosures = closures?.filter(c => new Date(c.closure_date) >= today).slice(0, 3) || [];
  // Filter out absences that fall on closure days (they're already shown as closures)
  const upcomingAbsences = mergedAbsences
    .filter(a => new Date(a.absence_date) >= today && !closureDates.has(a.absence_date))
    .sort((a, b) => a.absence_date.localeCompare(b.absence_date))
    .slice(0, 3);

  // Get upcoming activities (deduplicated by date+name)
  const upcomingActivities = useMemo(() => {
    const seen = new Set();
    return activityData.activityList
      .filter(a => a.date >= todayStr)
      .sort((a, b) => a.date.localeCompare(b.date))
      .filter(a => {
        const key = `${a.date}-${a.name}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 3);
  }, [activityData.activityList, todayStr]);

  // Get background style from preset (or use default)
  const headerStyle = backgroundPreset ? getBackgroundStyle(backgroundPreset) : {
    backgroundImage: 'linear-gradient(to bottom right, #3b82f6, #6366f1, #8b5cf6)'
  };

  return (
    <Card className="overflow-hidden border border-slate-200 shadow-lg rounded-3xl h-full flex flex-col">
      <CardHeader
        className="pb-3 pt-5 transition-all duration-500 ease-in-out"
        style={headerStyle}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg text-white drop-shadow-lg" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            Kalendarz
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={prevMonth}
              disabled={isAnimating}
              className="text-white hover:bg-white/20 disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span
              className={cn(
                "text-sm font-medium w-28 text-center text-white drop-shadow transition-all duration-300",
                isAnimating && slideDirection === 'left' && "opacity-0 translate-x-[-10px]",
                isAnimating && slideDirection === 'right' && "opacity-0 translate-x-[10px]"
              )}
              style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}
            >
              {format(currentMonth, 'LLLL yyyy', { locale: pl })}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={nextMonth}
              disabled={isAnimating}
              className="text-white hover:bg-white/20 disabled:opacity-50"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4 flex-1">
        {/* Legend */}
        <div className="flex items-center gap-4 mb-3 text-xs text-slate-500 flex-wrap">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-red-100 border border-red-300 rounded" />
            <span>Zamknięte</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-amber-100 border border-amber-300 rounded" />
            <span>Zgłoszona nieobecność</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-blue-100 border border-blue-300 rounded" />
            <span>Zajęcia</span>
          </div>
        </div>

        {/* Calendar Grid with Swipe Support */}
        <div
          ref={containerRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className="touch-pan-y select-none"
        >
          <div className="grid grid-cols-7 gap-1 text-center text-xs mb-2">
            {['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'].map(day => (
              <div key={day} className="font-medium text-slate-500 py-1">{day}</div>
            ))}
          </div>
          <div
            className={cn(
              "grid grid-cols-7 gap-1 transition-all duration-300 ease-out",
              isAnimating && slideDirection === 'left' && "opacity-0 translate-x-[-20px]",
              isAnimating && slideDirection === 'right' && "opacity-0 translate-x-[20px]"
            )}
            style={{
              transform: swipeOffset !== 0 ? `translateX(${swipeOffset * 0.3}px)` : undefined,
              opacity: swipeOffset !== 0 ? 1 - Math.abs(swipeOffset) / 200 : undefined
            }}
          >
          {/* Padding for first week */}
          {Array.from({ length: paddingDays }).map((_, i) => (
            <div key={`pad-${i}`} className="h-8" />
          ))}
          {/* Days */}
          {days.map(day => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const isClosed = closureDates.has(dateStr);
            const hasAbsence = absenceDates.has(dateStr);
            const hasActivity = activityData.activityDates.has(dateStr);
            const closure = closureMap[dateStr];
            const absence = absenceMap[dateStr];
            const activities = activityData.activityDates.get(dateStr) || [];
            const isWeekend = day.getDay() === 0 || day.getDay() === 6;
            const hasEvents = isClosed || hasAbsence || hasActivity;

            // Day cell content
            const dayCell = (
              <div
                className={cn(
                  "h-8 flex items-center justify-center text-sm rounded-md relative",
                  isToday(day) && "ring-2 ring-blue-500 ring-offset-1",
                  isClosed && "bg-red-100 text-red-700 font-medium",
                  hasAbsence && !isClosed && "bg-amber-50 text-amber-700",
                  isWeekend && !isClosed && !hasAbsence && "text-slate-400",
                  !isClosed && !hasAbsence && !isWeekend && "hover:bg-slate-100",
                  hasEvents && "cursor-pointer"
                )}
              >
                {day.getDate()}
                {/* Closure indicator (top-right red dot) */}
                {isClosed && (
                  <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full" />
                )}
                {/* Activity indicator (top-left blue dot) */}
                {hasActivity && (
                  <div className="absolute -top-0.5 -left-0.5 w-2 h-2 bg-blue-500 rounded-full" />
                )}
                {/* Absence indicator (bottom-right amber dot) */}
                {hasAbsence && !isClosed && (
                  <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-amber-500 rounded-full" />
                )}
              </div>
            );

            // If day has events, wrap in popover
            if (hasEvents) {
              const uniqueActivities = [...new Set(activities.map(a => a.name))];
              return (
                <Popover key={dateStr}>
                  <PopoverTrigger asChild>
                    {dayCell}
                  </PopoverTrigger>
                  <PopoverContent className="w-auto max-w-[200px] p-3" side="top" align="center">
                    <p className="font-medium text-sm mb-2">
                      {format(day, 'd MMMM', { locale: pl })}
                    </p>
                    <div className="space-y-1.5 text-sm">
                      {isClosed && (
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
                          <span className="text-red-700">{closure?.title || 'Zamknięte'}</span>
                        </div>
                      )}
                      {uniqueActivities.map((name, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                          <span className="text-blue-700">{name}</span>
                        </div>
                      ))}
                      {hasAbsence && !isClosed && (
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                          <span className="text-amber-700">{absence?.reason || 'Nieobecność'}</span>
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              );
            }

            return <div key={dateStr}>{dayCell}</div>;
          })}
          </div>
        </div>

        {/* Upcoming Events List */}
        {(upcomingClosures.length > 0 || upcomingAbsences.length > 0 || upcomingActivities.length > 0) && (
          <div className="mt-4 pt-4 border-t space-y-2">
            <p className="text-xs font-medium text-slate-500">Nadchodzące:</p>

            {/* Closures */}
            {upcomingClosures.map((closure, i) => (
              <div key={`c-${i}`} className="flex items-center gap-2 text-sm">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className="font-medium text-slate-700">
                  {format(new Date(closure.closure_date), 'd MMM', { locale: pl })}
                </span>
                <span className="text-slate-500">{closure.title}</span>
              </div>
            ))}

            {/* Activities */}
            {upcomingActivities.map((activity, i) => (
              <div key={`act-${i}`} className="flex items-center gap-2 text-sm">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <span className="font-medium text-slate-700">
                  {format(new Date(activity.date), 'd MMM', { locale: pl })}
                </span>
                <span className="text-slate-500">{activity.name}</span>
              </div>
            ))}

            {/* Absences */}
            {upcomingAbsences.map((absence, i) => (
              <div key={`a-${i}`} className="flex items-center gap-2 text-sm">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="font-medium text-slate-700">
                  {format(new Date(absence.absence_date), 'd MMM', { locale: pl })}
                </span>
                <span className="text-slate-500">
                  {absence.reason || 'Nieobecność'}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
